import React from "react";
import {
    FieldError as BaseFieldError,
    Form as BaseForm,
    TextField as BaseTextField,
    FieldErrorProps,
    Input,
    Label,
    Text,
    TextFieldProps,
    composeRenderProps,
} from "react-aria-components";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { IconButton } from "../Clickables";
import { LogoLoader } from "../LogoLoader";
import { RichText } from "../RichText";

import styles from "./inputs.module.scss";

function FieldError({ children, ...props }: FieldErrorProps) {
    if (children) return <BaseFieldError {...props}>{children}</BaseFieldError>;
    return (
        <BaseFieldError {...props}>
            {({ validationDetails, validationErrors }) => {
                if (validationDetails.valueMissing)
                    return "This field is required";
                return validationErrors;
            }}
        </BaseFieldError>
    );
}

function TextField({
    label,
    description,
    errorMessage,
    className,
    children,
    ...props
}: {
    errorMessage?: string;
    label: string;
    description?: string;
} & TextFieldProps) {
    return (
        <BaseTextField
            {...props}
            className={composeRenderProps(className, (className) =>
                concatClassNames([styles.textField, className]),
            )}
            isInvalid={errorMessage ? true : undefined}
        >
            {composeRenderProps(children, (children) => (
                <>
                    {description && (
                        <Text slot="description">{description}</Text>
                    )}
                    <div className={styles.fieldWrapper}>
                        <Label>{label}</Label>
                        <Input />
                        {children && (
                            <div className={styles.childWrapper}>
                                {children}
                            </div>
                        )}
                    </div>
                    <FieldError>{errorMessage}</FieldError>
                </>
            ))}
        </BaseTextField>
    );
}

/**
 * Force some properties to be required (e.g. not null/undefined)
 */
type SomeRequired<Type, Props extends keyof Type> = Omit<Type, Props> &
    Required<Pick<Type, Props>>;

type Props = SomeRequired<React.ComponentProps<typeof TextField>, "name"> & {
    isLoading?: boolean;
    isDone?: boolean;
    successMessage?: string;
    /**
     * A function that takes a form element as an argument and returns when
     * submission is successful. Errors, from validation or otherwise, should be thrown
     * so they'll be displayed to the user
     */
    orchestrateResponse: (form: HTMLFormElement) => Promise<void>;
};

export const InputForm = ({
    isLoading: isLoadingProps,
    isDone: isDoneProps,
    orchestrateResponse,
    successMessage,
    ...props
}: Props) => {
    const [errors, setErrors] = React.useState<{ [field: string]: string[] }>(
        {},
    );
    const [isLoadingState, setIsLoading] = React.useState(false);
    const [isDoneState, setIsDone] = React.useState(false);
    const isDone = typeof isDoneProps == "boolean" ? isDoneProps : isDoneState;
    const isLoading =
        typeof isLoadingProps == "boolean" ? isLoadingProps : isLoadingState;
    return (
        <BaseForm
            validationErrors={errors}
            onSubmit={async (e) => {
                e.preventDefault();
                setErrors({});
                setIsLoading(true);
                setIsDone(false);

                try {
                    await orchestrateResponse(e.target as HTMLFormElement);
                    setIsDone(true);
                } catch (errors) {
                    // should validate with io-ts ideally
                    if (typeof errors == "string" || errors instanceof Error) {
                        setErrors({ email: [String(errors)] });
                    } else {
                        // this form only has one visible field, so pretend (for the user) that all errors go with that field
                        setErrors({
                            email: Object.values(errors as object).flat(
                                2,
                            ) as string[],
                        });
                    }
                }
                setIsLoading(false);
            }}
        >
            {isDone ? (
                <RichText
                    value={
                        successMessage || "Thanks! You'll be hearing from us"
                    }
                />
            ) : (
                <TextField
                    {...props}
                    onBlur={(...x) => {
                        setErrors({});
                        if (props.onBlur) props.onBlur(...x);
                    }}
                >
                    {isLoading ? (
                        <LogoLoader className={styles.loader} />
                    ) : (
                        <IconButton icon="arrow" type="submit" label="Submit" />
                    )}
                </TextField>
            )}
        </BaseForm>
    );
};
